import React, { useEffect, useRef, useState } from "react";

type ScratchOffProps = {
  title: string;
  body: string;
  footer: string;
};

const ScratchOff: React.FC<ScratchOffProps> = ({ title, body, footer }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [revealed, setRevealed] = useState(false);
  const revealRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resize = () => {
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = "source-over";
      const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
      gradient.addColorStop(0, "#a9a9ad");
      gradient.addColorStop(0.35, "#e5e6ea");
      gradient.addColorStop(0.6, "#b7b8bf");
      gradient.addColorStop(1, "#d8d9de");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, rect.width, rect.height);

      const sheen = ctx.createLinearGradient(0, rect.height * 0.2, rect.width, rect.height * 0.8);
      sheen.addColorStop(0, "rgba(255,255,255,0)");
      sheen.addColorStop(0.45, "rgba(255,255,255,0.35)");
      sheen.addColorStop(0.55, "rgba(255,255,255,0.55)");
      sheen.addColorStop(0.7, "rgba(255,255,255,0.15)");
      sheen.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = sheen;
      ctx.fillRect(0, 0, rect.width, rect.height);

      const diagonal = ctx.createLinearGradient(rect.width, 0, 0, rect.height);
      diagonal.addColorStop(0, "rgba(70,70,80,0.25)");
      diagonal.addColorStop(0.5, "rgba(240,240,245,0.12)");
      diagonal.addColorStop(1, "rgba(120,120,130,0.18)");
      ctx.fillStyle = diagonal;
      ctx.fillRect(0, 0, rect.width, rect.height);

      // Add a little noise for texture
      for (let i = 0; i < 1500; i += 1) {
        const x = Math.random() * rect.width;
        const y = Math.random() * rect.height;
        ctx.fillStyle = `rgba(120,120,130,${Math.random() * 0.35})`;
        ctx.fillRect(x, y, 2, 2);
      }
      ctx.globalCompositeOperation = "destination-out";
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let isDrawing = false;
    let activeTouchId: number | null = null;

    const getPos = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        r: 18
      };
    };

    const scratch = (event: PointerEvent) => {
      if (!isDrawing) return;
      const { x, y, r } = getPos(event);
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      event.preventDefault();
    };

    const handleDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      isDrawing = true;
      if (canvas.setPointerCapture) {
        canvas.setPointerCapture(event.pointerId);
      }
      scratch(event);
    };

    const handleTouch = (event: TouchEvent) => {
      if (!isDrawing) return;
      const touch =
        activeTouchId === null
          ? event.touches[0]
          : Array.from(event.touches).find((t) => t.identifier === activeTouchId);
      if (!touch) return;
      const rect = canvas.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      const r = 18;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      event.preventDefault();
    };

    const handleTouchStart = (event: TouchEvent) => {
      isDrawing = true;
      activeTouchId = event.touches[0]?.identifier ?? null;
      handleTouch(event);
    };

    const checkReveal = () => {
      if (revealRef.current) return;
      const width = canvas.width;
      const height = canvas.height;
      if (width === 0 || height === 0) return;
      const image = ctx.getImageData(0, 0, width, height).data;
      let cleared = 0;
      for (let i = 3; i < image.length; i += 4) {
        if (image[i] === 0) cleared += 1;
      }
      const total = image.length / 4;
      const ratio = cleared / total;
      if (ratio >= 0.5) {
        revealRef.current = true;
        setRevealed(true);
      }
    };

    const handleTouchEnd = () => {
      isDrawing = false;
      activeTouchId = null;
      checkReveal();
    };

    const handleUp = () => {
      isDrawing = false;
      activeTouchId = null;
      checkReveal();
    };

    canvas.addEventListener("pointerdown", handleDown);
    canvas.addEventListener("pointermove", scratch);
    canvas.addEventListener("pointerup", handleUp);
    canvas.addEventListener("pointerleave", handleUp);
    canvas.addEventListener("pointercancel", handleUp);
    canvas.addEventListener("touchstart", handleTouchStart, { passive: false });
    canvas.addEventListener("touchmove", handleTouch, { passive: false });
    canvas.addEventListener("touchend", handleTouchEnd);
    canvas.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      canvas.removeEventListener("pointerdown", handleDown);
      canvas.removeEventListener("pointermove", scratch);
      canvas.removeEventListener("pointerup", handleUp);
      canvas.removeEventListener("pointerleave", handleUp);
      canvas.removeEventListener("pointercancel", handleUp);
      canvas.removeEventListener("touchstart", handleTouchStart);
      canvas.removeEventListener("touchmove", handleTouch);
      canvas.removeEventListener("touchend", handleTouchEnd);
      canvas.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, []);

  return (
    <div className="scratch-wrap" ref={containerRef}>
      <div className="coupon">
        <div className="coupon-inner">
          <p className="coupon-label">Redeemable Gift</p>
          <h2>{title}</h2>
          <p className="coupon-body">{body}</p>
          <div className="coupon-art" aria-hidden="true">
            <svg viewBox="0 0 140 180" role="img" aria-label="Perfume bottle">
              <rect x="52" y="10" width="36" height="24" rx="6" />
              <rect x="46" y="32" width="48" height="18" rx="6" />
              <rect x="30" y="50" width="80" height="100" rx="16" />
              <rect x="40" y="70" width="60" height="70" rx="12" />
              <circle cx="70" cy="120" r="16" />
            </svg>
          </div>
          <p className="coupon-footer">{footer}</p>
        </div>
      </div>
      <canvas
        className={`scratch-layer ${revealed ? "revealed" : ""}`}
        ref={canvasRef}
        aria-label="Scratch to reveal"
      />
      {!revealed && <div className="scratch-hint">Scratch to reveal</div>}
    </div>
  );
};

export default ScratchOff;
